import { ReactNode, useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ComponentLoader } from "../loader";
import {
  Column,
  Lineage as UILineage,
  ViewsCodeModalArgs,
  ApiHelper,
} from "@altimateai/ui-components/lineage";
import { Stack } from "@altimateai/lego";
import { getSnowflakeStage, getFinalLineageColumns, getFinalLineageTables } from "@/helpers/apis";
import { columnRkToTableRk, destructColumn } from "./utils";
import { useAppState } from "@/modules/app/useAppContext";
import type * as LineageApis from "@/helpers/apis";

export interface LineageProps {
  tableId: string;
  getStartingNode: typeof LineageApis.altimateToAmundsen;
  postColumns: typeof LineageApis.postColumns;
  upstreamTables: typeof LineageApis.upstreamTables;
  downstreamTables: typeof LineageApis.downstreamTables;
  postConnectedColumns: typeof LineageApis.postConnectedColumns;
  dbtInfo: typeof LineageApis.fetchDbtInfo;
  height?: string;
  setViewCodeModalArgs: (args: ViewsCodeModalArgs | null) => void;
  showViewCodeModal: boolean;
  renderTags?: (entity: { column: Column; table_rk: string }) => ReactNode;
  exportFinalLineage: boolean;
}

const LineageV2 = ({
  tableId,
  getStartingNode,
  postColumns,
  upstreamTables,
  downstreamTables,
  postConnectedColumns,
  dbtInfo,
  height = "76vh",
  setViewCodeModalArgs,
  showViewCodeModal,
  renderTags,
  exportFinalLineage = false,
}: LineageProps) => {
  const { staleResourcesEnabled } = useAppState();
  const [isApiHelperInitialized, setIsApiHelperInitialized] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const edgeVisibility = useRef({ showNonSelectEdges: true, showSelectEdges: true });

  const { data: tableData, isLoading } = useQuery({
    queryKey: [TABLE_MAPPER, tableId],
    queryFn: () => getStartingNode({ table_id: tableId }),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    const t = tableData;
    if (!t) return;

    const renderNode = {
      aiEnabled: true,
      showCodeModal: showViewCodeModal,
      config: { exportFinalLineage },
      node: {
        table: t.table,
        upstreamCount: t.upstream_count,
        downstreamCount: t.downstream_count,
        label: t.label,
        nodeType: t.resource_type?.split(":")?.[0] || t.resource_type,
        isExternalProject: false,
        tests: Array.isArray(t.tests)
          ? t.tests.map((test: unknown) => ({ key: (test as { name: string }).name }))
          : [],
        materialization: t.materialization,
        schema: t.schema_name,
        stale: staleResourcesEnabled && (t.is_stale || false),
        lastSeen: staleResourcesEnabled ? t.last_seen || null : null,
      },
    };
    // @ts-expect-error valid
    ApiHelper.get = async (url, data) => {
      console.log("ApiHelper", url, data);
      switch (url) {
        case "upstreamTables": {
          if (!data?.table) {
            return { tables: [] };
          }
          setShowLoader(true);
          const result = await upstreamTables(data.table as string);
          setShowLoader(false);
          if (!result) {
            return { tables: [] };
          }
          return {
            tables: result.tables.map(t => ({
              id: t.id,
              table: t.table,
              upstreamCount: t.upstream_count,
              downstreamCount: t.downstream_count,
              label: t.label,
              nodeType: t.resource_type,
              isExternalProject: false,
              tests: Array.isArray(t.tests)
                ? t.tests.map((test: unknown) => ({ key: (test as { name: string }).name }))
                : [],
              materialization: t.materialization,
              schema: t.schema_name,
              source: t.source,
              stale: staleResourcesEnabled && (t.is_stale || false),
              lastSeen: staleResourcesEnabled ? t.last_seen || null : null,
            })),
          };
        }
        case "downstreamTables": {
          if (!data?.table) {
            return { tables: [] };
          }
          setShowLoader(true);
          const result = await downstreamTables(data.table as string);
          setShowLoader(false);
          if (!result) {
            return { tables: [] };
          }
          return {
            tables: result.tables.map(t => ({
              id: t.id,
              table: t.table,
              upstreamCount: t.upstream_count,
              downstreamCount: t.downstream_count,
              label: t.label,
              nodeType: t.resource_type,
              isExternalProject: false,
              tests: Array.isArray(t.tests)
                ? t.tests.map((test: unknown) => ({ key: (test as { name: string }).name }))
                : [],
              materialization: t.materialization,
              schema: t.schema_name,
              source: t.source,
              stale: staleResourcesEnabled && (t.is_stale || false),
              lastSeen: staleResourcesEnabled ? t.last_seen || null : null,
            })),
          };
        }
        case "getColumns": {
          setShowLoader(true);
          const result = await postColumns({
            table_id: (data?.table as string) || "",
          });
          setShowLoader(false);
          if (!result) {
            return { columns: [], purpose: "" };
          }
          return {
            purpose: result.purpose,
            columns: result.columns.map(c => ({
              ...c,
              name: c.name,
              table: columnRkToTableRk(c.rk),
              datatype: c.datatype,
              can_lineage_expand: c.can_lineage_expand,
              is_transformation: c.is_transformation,
              description: c.description,
            })),
          };
        }
        case "getDbtInfo": {
          setShowLoader(true);
          if (!data?.table_rk) {
            return undefined;
          }
          const result = await dbtInfo({
            table_rk: (data as { table_rk: string }).table_rk,
          });
          setShowLoader(false);
          return result;
        }
        case "getConnectedColumns": {
          const connectedData = data as {
            targets: Array<[string, string]>;
            currAnd1HopTables: string[];
            upstreamExpansion: boolean;
            showIndirectEdges: boolean;
          };
          const result = data
            ? await postConnectedColumns({
                column_fqns: connectedData.targets.map(t => `${t[0]}/${t[1]}`),
                nodes: connectedData.currAnd1HopTables,
                upstreamExpansion: connectedData.upstreamExpansion,
                showIndirectEdges: connectedData.showIndirectEdges,
              })
            : null;
          const column_lineage: {
            source: string[];
            target: string[];
            type: string;
            viewsType: string;
            viewsCode: string;
          }[] = [];
          if (!result) return { column_lineage };

          const { highlight_edges } = result as {
            highlight_edges: {
              extra: { type: string; lens_code: string; lens_type: string };
              source: string;
              target: string;
            }[];
          };
          for (const e of highlight_edges) {
            const type = e.extra?.type || "direct";
            if (type === "direct" && !edgeVisibility.current.showSelectEdges) continue;
            if (type === "indirect" && !edgeVisibility.current.showNonSelectEdges) continue;
            const lens_code = e.extra?.lens_code;
            const lens_type = e.extra?.lens_type;
            column_lineage.push({
              source: destructColumn(e["source"]),
              target: destructColumn(e["target"]),
              type: type,
              viewsType: lens_type,
              viewsCode: lens_code,
            });
          }

          return { column_lineage };
        }
        case "getLineageSettings":
          return {
            ...edgeVisibility.current,
            defaultExpansion: 1,
          };
        case "columnLineage": {
          if ((data as { event: "start" | "end" })?.event === "start") setShowLoader(true);
          else if ((data as { event: "start" | "end" })?.event === "end") setShowLoader(false);
          return;
        }
        case "init": {
          const event = new CustomEvent("renderStartNode", {
            detail: renderNode,
          });
          document.dispatchEvent(event);
          return;
        }
        case "persistLineageSettings": {
          edgeVisibility.current = { ...edgeVisibility.current, ...data };
          return;
        }
        case "showViewCodeModal": {
          setViewCodeModalArgs(data as unknown as ViewsCodeModalArgs);
          return;
        }
        case "getSnowflakeStageDetails": {
          const result = await getSnowflakeStage(data as { table_rk: string });
          return result;
        }
        case "finalColumnsDownload": {
          await getFinalLineageColumns(data as Parameters<typeof getFinalLineageColumns>["0"]);
          return;
        }
        case "finalTablesDownload": {
          if (
            !data ||
            typeof data.table_rk !== "string" ||
            typeof data.upstream !== "boolean" ||
            typeof data.downstream !== "boolean"
          ) {
            console.error("ApiHelper finalTablesDownload: unexpected data shape", data);
            return;
          }
          await getFinalLineageTables(data as Parameters<typeof getFinalLineageTables>["0"]);
          return;
        }
        case "getExposureDetails":
        case "sendFeedback":
        case "openFile":
        case "openChat":
        case "showInfoNotification":
        case "previewFeature":
        case "telemetryEvents":
        default:
          break;
      }
    };
    setIsApiHelperInitialized(true);
  }, [
    tableData,
    showViewCodeModal,
    exportFinalLineage,
    staleResourcesEnabled,
    setViewCodeModalArgs,
    upstreamTables,
    downstreamTables,
    postColumns,
    dbtInfo,
    postConnectedColumns,
  ]);

  return (
    <div className={"al-h-[80vh] al-relative"} style={{ height: height }}>
      <Stack align="baseline" className={"al-absolute al-right-[16px] al-z-1"}>
        {/* <div id="expand-container" /> */}
        <div id="settings-container" />
        <div id="reset-container" />
      </Stack>
      {(isLoading || showLoader) && <ComponentLoader label={"Loading..."} />}
      {isApiHelperInitialized && (
        <UILineage
          theme={"light"}
          dynamicLineage={{ aiEnabled: true }}
          lineageType={"dynamic"}
          renderTags={renderTags}
          client="saas"
        />
      )}
    </div>
  );
};

const TABLE_MAPPER = "TABLE-MAPPER";

export { LineageV2 as Lineage };
